/**
 * Module: screens/pantene/_uploadXHR
 * Role: XHR-based file upload with progress tracking for the Pantene video
 *       upload screen. fetch() lacks upload progress events; XHR provides
 *       xhr.upload.onprogress.
 *
 * Uses: (none — pure browser API)
 * Used by: screens/pantene/VideoUpload.tsx
 */

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface UploadXHROptions {
  url: string;
  file: File;
  onProgress: (progress: UploadProgress) => void;
  signal?: AbortSignal;
}

/**
 * PUT a file to a presigned URL via XHR with progress events.
 * Resolves on HTTP 2xx, rejects on any other status or network error.
 */
export function uploadXHR({ url, file, onProgress, signal }: UploadXHROptions): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        onProgress({ loaded: e.loaded, total: e.total });
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`PUT failed: HTTP ${xhr.status}`));
      }
    };

    xhr.onerror = () => {
      reject(new Error('PUT failed: network error'));
    };

    xhr.onabort = () => {
      reject(new DOMException('Upload aborted', 'AbortError'));
    };

    if (signal) {
      if (signal.aborted) {
        reject(new DOMException('Upload aborted', 'AbortError'));
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}
